import React, { useState, useEffect } from 'react'
import Select from 'react-select'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { Formik } from 'formik'
import * as yup from 'yup'
import pdfMake from '../../../utils/pdfMakeInit'
import { alpha, useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { toast } from 'react-toastify'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import AppBar from '@mui/material/AppBar'
import Radio from '@mui/material/Radio'
import RadioGroup from '@mui/material/RadioGroup'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormControl from '@mui/material/FormControl'
import Alert from '@mui/material/Alert'
import Chip from '@mui/material/Chip'
import Divider from '@mui/material/Divider'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import EditIcon from '@mui/icons-material/Edit'
import AddIcon from '@mui/icons-material/Add'
import DownloadIcon from '@mui/icons-material/Download'
import Container from '../../GeneralComponents/Container'
import { Topbar, Sidebar } from '../components'
import setGlobalLoader from '../../../redux/actions/loaderActions'
import { getOrganizationById, getStudents, createInvoice, addInvoicePayment, getInvoice, updateInvoicePayment, getRecentInvoices, updateInvoice } from '../../../redux/actions/apiActions'
import { buildInvoicePDF } from '../../../utils/invoiceTemplate'
import { urlToBase64 } from '../../../utils/urlToBase64'


const firstInvoiceSchema = yup.object({
  originalAmount: yup.number().required('Original course fee is required.').min(1, 'Must be positive.'),
  discount: yup
    .number()
    .min(0, 'Must be 0 or positive.')
    .nullable()
    .test('discount-lt-original', 'Discount cannot be equal to or more than the original fee.', function (value) {
      const { originalAmount } = this.parent
      if (!value || !originalAmount) return true
      return Number(value) < Number(originalAmount)
    }),
  amountReceived: yup.number().min(0, 'Must be positive.').nullable(),
  paymentMode: yup.string().required('Payment mode is required.'),
})

const addPaymentSchema = yup.object({
  amountReceived: yup.number().required('Amount received is required.').min(1, 'Must be positive.'),
  paymentMode: yup.string().required('Payment mode is required.'),
})

const statusColors = { PAID: 'success', PARTIAL: 'warning' }

const CreateInvoice = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const dispatch = useDispatch()
  const theme = useTheme()
  const user = useSelector(state => state.authReducer.user)
  const isMd = useMediaQuery(theme.breakpoints.up('md'), { defaultMatches: true })

  const [organizationDetails, setOrganizationDetails] = useState(null)
  const [students, setStudents] = useState([])
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [existingInvoice, setExistingInvoice] = useState(null)
  const [loadingInvoice, setLoadingInvoice] = useState(false)
  const [recentInvoices, setRecentInvoices] = useState([])
  const [openSidebar, setOpenSidebar] = useState(false)

  // Edit payment dialog
  const [editPaymentDialog, setEditPaymentDialog] = useState(false)
  const [editPaymentRow, setEditPaymentRow] = useState(null)
  const [editPaymentAmount, setEditPaymentAmount] = useState('')
  const [editPaymentMode, setEditPaymentMode] = useState('CASH')
  const [editPaymentSaving, setEditPaymentSaving] = useState(false)

  // Edit total dialog
  const [editTotalDialog, setEditTotalDialog] = useState(false)
  const [editOriginalAmount, setEditOriginalAmount] = useState('')
  const [editDiscount, setEditDiscount] = useState('')
  const [editTotalSaving, setEditTotalSaving] = useState(false)

  const handleSidebarOpen = () => setOpenSidebar(true)
  const handleSidebarClose = () => setOpenSidebar(false)
  const open = isMd ? false : openSidebar

  useEffect(() => {
    if (user?._id) {
      dispatch(setGlobalLoader(true))
      async function fetchData() {
        try {
          const [orgRes, studentRes, recentRes] = await Promise.all([
            getOrganizationById(user._id),
            getStudents('?limit=10000'),
            getRecentInvoices(),
          ])
          setOrganizationDetails(orgRes?.data?.data)
          setStudents((studentRes?.data?.data?.docs || []).map(d => ({
            label: `${d?.name} - ${d?.phoneNumber || ''}`,
            value: d?._id,
            data: d,
          })))
          setRecentInvoices(recentRes?.data?.data || [])
          dispatch(setGlobalLoader(false))
        } catch (err) {
          dispatch(setGlobalLoader(false))
          toast.error('Error loading data.')
        }
      }
      fetchData()
    }
  }, [user?._id, dispatch])

  useEffect(() => {
    const s = location.state?.student
    if (!s?._id) return
    setSelectedStudent({ label: `${s.name} - ${s.phoneNumber || ''}`, value: s._id, data: s })
    setLoadingInvoice(true)
    getInvoice(`?studentId=${s._id}`)
      .then(res => setExistingInvoice(res?.data?.data || null))
      .catch(err => console.log('invoice fetch error', err))
      .finally(() => setLoadingInvoice(false))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleStudentChange = async (option) => {
    setSelectedStudent(option)
    setExistingInvoice(null)
    if (!option?.value) return
    setLoadingInvoice(true)
    try {
      const res = await getInvoice(`?studentId=${option.value}`)
      setExistingInvoice(res?.data?.data || null)
    } catch (err) {
      console.log('invoice fetch error', err)
    } finally {
      setLoadingInvoice(false)
    }
  }

  const fmt = (n) => (n ?? 0).toLocaleString('en-IN')
  const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '—'

  const fetchLogoBase64 = async (logoUrl) => {
    if (!logoUrl) return null
    try {
      return await urlToBase64(logoUrl)
    } catch {
      return null
    }
  }

  const handleSubmit = async (values, { resetForm }) => {
    const student = selectedStudent?.data
    const org = organizationDetails
    if (!student || !org) { toast.error('Missing student or organization details.'); return }

    const amountReceived = Number(values.amountReceived) || 0

    try {
      let updatedInvoice

      if (!existingInvoice) {
        const originalAmount = Number(values.originalAmount)
        const discount = Number(values.discount) || 0
        const totalAmount = originalAmount - discount

        if (totalAmount <= 0) {
          toast.error('Final amount after discount must be greater than 0.')
          return
        }

        await createInvoice({
          studentId: selectedStudent.value,
          originalAmount,
          discount,
          totalAmount,
          amountReceived: amountReceived || undefined,
          paymentMode: amountReceived > 0 ? values.paymentMode : undefined,
        })
        const invRes = await getInvoice(`?studentId=${selectedStudent.value}`)
        updatedInvoice = invRes?.data?.data
      } else {
        const res = await addInvoicePayment(existingInvoice._id, {
          amountReceived,
          paymentMode: values.paymentMode,
        })
        updatedInvoice = res?.data?.data
      }

      toast.success('Payment recorded.')

      if (updatedInvoice && amountReceived > 0) {
        const newPayment = { amountReceived, paymentMode: values.paymentMode }
        const invoiceForPDF = existingInvoice
          ? { ...existingInvoice }
          : { ...updatedInvoice, payments: [], totalPaid: 0, balanceDue: updatedInvoice.totalAmount }

        const logoBase64 = await fetchLogoBase64(org.logo)
        const doc = buildInvoicePDF(invoiceForPDF, student, org, newPayment, logoBase64)
        const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        pdfMake.createPdf(doc).download(`Invoice-${student.name}-${today}.pdf`)
        toast.success('Invoice downloaded.')
      }

      setExistingInvoice(updatedInvoice)
      getRecentInvoices().then(r => setRecentInvoices(r?.data?.data || [])).catch(() => {})
      resetForm()
    } catch (err) {
      const msg = err?.response?.data?.message || 'Something went wrong.'
      toast.error(msg)
    }
  }

  const handleEditPaymentOpen = (payment) => {
    setEditPaymentRow(payment)
    setEditPaymentAmount(String(payment.amountReceived))
    setEditPaymentMode(payment.paymentMode)
    setEditPaymentDialog(true)
  }

  const handleEditPaymentSave = async () => {
    if (!editPaymentRow || !existingInvoice) return
    const amount = Number(editPaymentAmount)
    if (isNaN(amount) || amount <= 0) { toast.error('Enter a valid amount.'); return }
    setEditPaymentSaving(true)
    try {
      const res = await updateInvoicePayment(existingInvoice._id, editPaymentRow._id, {
        amountReceived: amount,
        paymentMode: editPaymentMode,
      })
      setExistingInvoice(res?.data?.data)
      toast.success('Payment updated.')
      setEditPaymentDialog(false)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error updating payment.')
    } finally {
      setEditPaymentSaving(false)
    }
  }

  const handleEditTotalOpen = () => {
    setEditOriginalAmount(String(existingInvoice.originalAmount || existingInvoice.totalAmount))
    setEditDiscount(String(existingInvoice.discount || 0))
    setEditTotalDialog(true)
  }

  const handleEditTotalSave = async () => {
    const originalAmount = Number(editOriginalAmount)
    const discount = Number(editDiscount) || 0
    const totalAmount = originalAmount - discount

    if (!originalAmount || originalAmount <= 0) { toast.error('Enter a valid original fee.'); return }
    if (discount < 0) { toast.error('Discount cannot be negative.'); return }
    if (discount >= originalAmount) { toast.error('Discount cannot be equal to or more than the original fee.'); return }
    if (totalAmount <= 0) { toast.error('Final amount after discount must be greater than 0.'); return }
    if (totalAmount < existingInvoice.totalPaid) {
      toast.error(`Total cannot be less than amount already paid (₹${fmt(existingInvoice.totalPaid)}).`)
      return
    }

    setEditTotalSaving(true)
    try {
      const res = await updateInvoice(existingInvoice._id, { originalAmount, discount, totalAmount })
      setExistingInvoice(res?.data?.data)
      getRecentInvoices().then(r => setRecentInvoices(r?.data?.data || [])).catch(() => {})
      toast.success('Invoice updated.')
      setEditTotalDialog(false)
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Error updating invoice.')
    } finally {
      setEditTotalSaving(false)
    }
  }

  const handleRedownload = async () => {
    const student = selectedStudent?.data
    const org = organizationDetails
    if (!student || !org || !existingInvoice?.payments?.length) {
      toast.error('No payments found to re-download invoice for.')
      return
    }
    const payments = existingInvoice.payments
    const lastPayment = payments[payments.length - 1]
    const prevPayments = payments.slice(0, -1)
    const prevTotalPaid = prevPayments.reduce((sum, p) => sum + p.amountReceived, 0)
    const invoiceForPDF = {
      ...existingInvoice,
      payments: prevPayments,
      totalPaid: prevTotalPaid,
      balanceDue: existingInvoice.totalAmount - prevTotalPaid,
    }
    const logoBase64 = await fetchLogoBase64(org.logo)
    const doc = buildInvoicePDF(invoiceForPDF, student, org, {
      amountReceived: lastPayment.amountReceived,
      paymentMode: lastPayment.paymentMode,
    }, logoBase64)
    const today = new Date().toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
    pdfMake.createPdf(doc).download(`Invoice-${student.name}-${today}.pdf`)
    toast.success('Invoice downloaded.')
  }

  const isFirstInvoice = !existingInvoice
  const balanceDue = existingInvoice?.balanceDue ?? null

  // Computed value for edit-total dialog preview
  const editComputedTotal = (Number(editOriginalAmount) || 0) - (Number(editDiscount) || 0)

  return (
    <Box>
      <AppBar position={'fixed'} sx={{ backgroundColor: theme.palette.background.paper, borderBottom: `1px solid ${alpha(theme.palette.divider, 0.1)}` }} elevation={0}>
        <Container maxWidth={1} paddingY={{ xs: 1, sm: 1.5 }}>
          <Topbar onSidebarOpen={handleSidebarOpen} />
        </Container>
      </AppBar>
      <Sidebar onClose={handleSidebarClose} open={open} variant={isMd ? 'permanent' : 'temporary'} />
      <main>
        <Box height={{ xs: 94, sm: 102, md: 107 }} />
        <br /><br />
        <Box overflow="hidden" marginLeft={{ md: '270px' }} paddingX={{ xs: 2, md: 4 }}>
          <Typography variant={'h5'} sx={{ marginBottom: 2 }} fontWeight={700}>Create Invoice</Typography>

          <Alert severity="info" sx={{ marginBottom: 3, border: '1px solid', borderColor: 'info.main', '& .MuiAlert-message': { fontWeight: 500 } }}>
            The student must already be added to the system before creating an invoice. Use <strong>Add Student</strong> if they aren't listed yet.
          </Alert>

          {/* Recent invoices */}
          {recentInvoices.length > 0 && !location.state?.student && (
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1.5 }}>Recents</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {recentInvoices.map(inv => (
                  <Box
                    key={inv._id}
                    onClick={() => {
                      const s = inv.studentId
                      if (!s) return
                      handleStudentChange({ label: `${s.name} - ${s.phoneNumber || ''}`, value: s._id, data: s })
                    }}
                    sx={{
                      border: '1px solid',
                      borderColor: inv.status === 'PAID' ? 'success.light' : 'warning.light',
                      borderRadius: 2,
                      p: 1.5,
                      minWidth: 170,
                      maxWidth: 210,
                      cursor: 'pointer',
                      bgcolor: inv.status === 'PAID' ? 'success.50' : 'warning.50',
                      transition: 'box-shadow 0.15s',
                      '&:hover': { boxShadow: 3 },
                    }}
                  >
                    <Typography variant="body2" fontWeight={700} noWrap>{inv.studentId?.name || '—'}</Typography>
                    <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ mb: 0.5 }}>
                      {inv.studentId?.phoneNumber || ''}
                    </Typography>
                    <Box display="flex" alignItems="center" justifyContent="space-between">
                      <Chip
                        label={inv.status}
                        size="small"
                        color={inv.status === 'PAID' ? 'success' : 'warning'}
                        sx={{ height: 18, fontSize: 10 }}
                      />
                      <Typography variant="caption" fontWeight={600}>
                        &#8377;{fmt(inv.totalAmount)}
                      </Typography>
                    </Box>
                    {inv.status !== 'PAID' && (
                      <Typography variant="caption" color="error.main" display="block" sx={{ mt: 0.5 }}>
                        Due: &#8377;{fmt(inv.balanceDue)}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.disabled" display="block" sx={{ mt: 0.5 }}>
                      {fmtDate(inv.updatedAt)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Student selector */}
          <Box sx={{ maxWidth: 520, mb: 3 }}>
            <Typography variant={'subtitle2'} sx={{ mb: 1 }} fontWeight={700}>Select Student *</Typography>
            <Box display="flex" gap={1} alignItems="flex-start">
              <Box flex={1}>
                <Select
                  styles={{
                    menuPortal: base => ({ ...base, zIndex: 9999 }),
                    control: p => ({ ...p, minHeight: 40 }),
                  }}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  placeholder="Search and select student..."
                  value={selectedStudent}
                  options={students}
                  isClearable
                  isSearchable
                  onChange={handleStudentChange}
                />
              </Box>
              <Button variant="outlined" size="small" startIcon={<AddIcon />} onClick={() => navigate('/students/add')} sx={{ whiteSpace: 'nowrap', mt: '1px' }}>
                Add Student
              </Button>
            </Box>
          </Box>

          {/* Invoice summary (returning student) */}
          {loadingInvoice && <Typography variant="body2" color="text.secondary" mb={2}>Loading invoice...</Typography>}

          {existingInvoice && (
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2.5, mb: 3, bgcolor: 'grey.50', maxWidth: 600 }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={2}>
                <Typography variant="subtitle2" fontWeight={700}>Invoice Summary</Typography>
                <Chip label={existingInvoice.status} color={statusColors[existingInvoice.status]} size="small" />
              </Box>

              {/* Discount breakdown row (if discount exists) */}
              {existingInvoice.originalAmount && existingInvoice.discount > 0 && (
                <Box sx={{ mb: 2, p: 1.5, bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.light', borderRadius: 1.5 }}>
                  <Box display="flex" gap={3} flexWrap="wrap">
                    <Box>
                      <Typography variant="caption" color="text.secondary">Original Fee</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        &#8377;{fmt(existingInvoice.originalAmount)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Discount</Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.dark">
                        - &#8377;{fmt(existingInvoice.discount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}

              <Box display="flex" gap={4} flexWrap="wrap" mb={existingInvoice.payments?.length > 0 ? 2 : 0}>
                <Box>
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Typography variant="caption" color="text.secondary">
                      {existingInvoice.discount > 0 ? 'Total After Discount' : 'Total Fee'}
                    </Typography>
                    <EditIcon
                      sx={{ fontSize: 13, cursor: 'pointer', color: 'text.disabled', '&:hover': { color: 'primary.main' } }}
                      onClick={handleEditTotalOpen}
                      titleAccess="Edit total"
                    />
                  </Box>
                  <Typography variant="body1" fontWeight={700}>&#8377;{fmt(existingInvoice.totalAmount)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Total Paid</Typography>
                  <Typography variant="body1" fontWeight={700} color="success.main">&#8377;{fmt(existingInvoice.totalPaid)}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Balance Due</Typography>
                  <Typography variant="body1" fontWeight={700} color={existingInvoice.balanceDue > 0 ? 'error.main' : 'success.main'}>
                    &#8377;{fmt(existingInvoice.balanceDue)}
                  </Typography>
                </Box>
              </Box>

              {existingInvoice.payments?.length > 0 && (
                <>
                  <Divider sx={{ mb: 1.5 }} />
                  <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" mb={1}>Payment History</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <thead>
                        <tr style={{ background: '#f0f0f0' }}>
                          {['#', 'Date', 'Paid', 'Balance After', 'Mode', ''].map(h => (
                            <th key={h} style={{ padding: '6px 10px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(() => {
                          let running = existingInvoice.totalAmount
                          return existingInvoice.payments.map((p, i) => {
                            running -= p.amountReceived
                            return (
                              <tr key={p._id} style={{ borderTop: '1px solid #eee' }}>
                                <td style={{ padding: '6px 10px' }}>{i + 1}</td>
                                <td style={{ padding: '6px 10px', whiteSpace: 'nowrap' }}>{fmtDate(p.createdAt)}</td>
                                <td style={{ padding: '6px 10px', color: '#2e7d32' }}>&#8377;{fmt(p.amountReceived)}</td>
                                <td style={{ padding: '6px 10px', color: running > 0 ? '#d32f2f' : '#2e7d32', fontWeight: 600 }}>&#8377;{fmt(running)}</td>
                                <td style={{ padding: '6px 10px' }}>{p.paymentMode}</td>
                                <td style={{ padding: '6px 10px' }}>
                                  <EditIcon
                                    sx={{ fontSize: 16, cursor: 'pointer', color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                                    onClick={() => handleEditPaymentOpen(p)}
                                  />
                                </td>
                              </tr>
                            )
                          })
                        })()}
                      </tbody>
                    </table>
                  </Box>
                  <Box mt={2}>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<DownloadIcon />}
                      onClick={handleRedownload}
                    >
                      Re-download Last Invoice
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* No invoice yet */}
          {selectedStudent && !loadingInvoice && !existingInvoice && (
            <Box display="flex" alignItems="center" gap={1} mb={3}>
              <Chip label="No invoice yet" size="small" variant="outlined" />
              <Typography variant="body2" color="text.secondary">First time — set the course fee below.</Typography>
            </Box>
          )}

          {/* Form — only show when student is selected */}
          {selectedStudent && !loadingInvoice && (existingInvoice ? existingInvoice.status !== 'PAID' : true) && (
            <Formik
              initialValues={{ originalAmount: '', discount: '', amountReceived: '', paymentMode: 'CASH' }}
              validationSchema={isFirstInvoice ? firstInvoiceSchema : addPaymentSchema}
              onSubmit={handleSubmit}
              enableReinitialize
            >
              {({ values, errors, touched, setFieldValue, handleSubmit: formSubmit, isSubmitting }) => {
                const computedTotal = isFirstInvoice
                  ? (Number(values.originalAmount) || 0) - (Number(values.discount) || 0)
                  : null

                const maxReceivable = isFirstInvoice
                  ? Math.max(computedTotal || 0, 0)
                  : (balanceDue ?? 0)

                const previewBalance = isFirstInvoice
                  ? (computedTotal || 0) - (Number(values.amountReceived) || 0)
                  : (balanceDue ?? 0) - (Number(values.amountReceived) || 0)

                return (
                  <form onSubmit={formSubmit}>
                    <Grid container spacing={{ xs: 2, md: 3 }} sx={{ maxWidth: 700 }}>
                      {isFirstInvoice && (
                        <>
                          <Grid item xs={12} sm={6}>
                            <Typography variant={'subtitle2'} sx={{ mb: 1 }} fontWeight={700}>Original Course Fee (₹) *</Typography>
                            <TextField
                              variant="outlined" fullWidth size="small" type="number"
                              placeholder="e.g. 12000"
                              InputProps={{ inputProps: { min: 1 } }}
                              value={values.originalAmount}
                              onChange={e => setFieldValue('originalAmount', e.target.value)}
                              error={touched.originalAmount && Boolean(errors.originalAmount)}
                              helperText={touched.originalAmount && errors.originalAmount}
                            />
                          </Grid>

                          <Grid item xs={12} sm={6}>
                            <Typography variant={'subtitle2'} sx={{ mb: 1 }} fontWeight={700}>Discount (₹) <Typography component="span" variant="caption" color="text.secondary">optional</Typography></Typography>
                            <TextField
                              variant="outlined" fullWidth size="small" type="number"
                              placeholder="e.g. 2000"
                              InputProps={{ inputProps: { min: 0 } }}
                              value={values.discount}
                              onChange={e => setFieldValue('discount', e.target.value)}
                              error={touched.discount && Boolean(errors.discount)}
                              helperText={touched.discount && errors.discount}
                            />
                          </Grid>

                          {values.originalAmount > 0 && (
                            <Grid item xs={12}>
                              <Box sx={{ display: 'inline-flex', gap: 3, p: 1.5, bgcolor: 'primary.50', borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light' }}>
                                {Number(values.discount) > 0 && (
                                  <>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Original Fee</Typography>
                                      <Typography variant="body2" fontWeight={600} sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                                        &#8377;{fmt(Number(values.originalAmount))}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">Discount</Typography>
                                      <Typography variant="body2" fontWeight={600} color="warning.dark">- &#8377;{fmt(Number(values.discount))}</Typography>
                                    </Box>
                                  </>
                                )}
                                <Box>
                                  <Typography variant="caption" color="text.secondary">Final Agreed Total</Typography>
                                  <Typography variant="body1" fontWeight={700} color={computedTotal > 0 ? 'primary.main' : 'error.main'}>
                                    &#8377;{fmt(computedTotal)}
                                  </Typography>
                                </Box>
                              </Box>
                              {computedTotal <= 0 && values.originalAmount > 0 && (
                                <Typography variant="caption" color="error" display="block" mt={0.5}>
                                  Discount cannot exceed or equal the original fee.
                                </Typography>
                              )}
                            </Grid>
                          )}
                        </>
                      )}

                      <Grid item xs={12} sm={6}>
                        <Typography variant={'subtitle2'} sx={{ mb: 1 }} fontWeight={700}>
                          {isFirstInvoice ? 'First Payment (₹)' : 'Amount Received (₹) *'}
                          {isFirstInvoice && <Typography component="span" variant="caption" color="text.secondary"> optional</Typography>}
                        </Typography>
                        <TextField
                          variant="outlined" fullWidth size="small" type="number"
                          placeholder={maxReceivable > 0 ? `Max ${fmt(maxReceivable)}` : 'Amount'}
                          InputProps={{ inputProps: { min: 1, max: maxReceivable || undefined } }}
                          value={values.amountReceived}
                          onChange={e => {
                            const val = Number(e.target.value)
                            setFieldValue('amountReceived', maxReceivable > 0 && val > maxReceivable ? maxReceivable : e.target.value)
                          }}
                          error={touched.amountReceived && Boolean(errors.amountReceived)}
                          helperText={touched.amountReceived && errors.amountReceived}
                        />
                      </Grid>

                      {values.amountReceived > 0 && previewBalance >= 0 && (
                        <Grid item xs={12} sm={6}>
                          <Typography variant={'subtitle2'} sx={{ mb: 1 }} fontWeight={700}>Balance After This Payment</Typography>
                          <Typography variant="h6" fontWeight={700} color={previewBalance > 0 ? 'error.main' : 'success.main'}>
                            &#8377; {fmt(previewBalance)}
                          </Typography>
                        </Grid>
                      )}

                      <Grid item xs={12} sm={6}>
                        <Typography variant={'subtitle2'} sx={{ mb: 1 }} fontWeight={700}>Payment Mode *</Typography>
                        <FormControl>
                          <RadioGroup row value={values.paymentMode} onChange={e => setFieldValue('paymentMode', e.target.value)}>
                            <FormControlLabel value="CASH" control={<Radio />} label="Cash" />
                            <FormControlLabel value="UPI" control={<Radio />} label="UPI" />
                          </RadioGroup>
                        </FormControl>
                        {touched.paymentMode && errors.paymentMode && (
                          <Typography variant="caption" color="error">{errors.paymentMode}</Typography>
                        )}
                      </Grid>
                    </Grid>

                    <Box sx={{ mt: 4, mb: 4 }}>
                      <Button variant={'contained'} type="submit" size="large" disabled={isSubmitting || (isFirstInvoice && computedTotal <= 0)}>
                        {isFirstInvoice ? 'Create Invoice' : 'Record Payment & Download Invoice'}
                      </Button>
                    </Box>
                  </form>
                )
              }}
            </Formik>
          )}

          {selectedStudent && existingInvoice?.status === 'PAID' && (
            <Alert severity="success" sx={{ maxWidth: 500 }}>
              This student has fully paid their course fee of &#8377;{fmt(existingInvoice.totalAmount)}.
            </Alert>
          )}
        </Box>
      </main>

      {/* Edit Payment Dialog */}
      <Dialog open={editPaymentDialog} onClose={() => setEditPaymentDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2}>
            Editing payment #{existingInvoice?.payments?.findIndex(p => p._id === editPaymentRow?._id) + 1 || ''}
          </Typography>
          <TextField
            label="Amount Received (₹)"
            type="number"
            fullWidth
            value={editPaymentAmount}
            onChange={(e) => setEditPaymentAmount(e.target.value)}
            InputProps={{ inputProps: { min: 1 } }}
            sx={{ mb: 2 }}
            autoFocus
          />
          <Typography variant="subtitle2" fontWeight={700} mb={1}>Payment Mode</Typography>
          <FormControl>
            <RadioGroup row value={editPaymentMode} onChange={(e) => setEditPaymentMode(e.target.value)}>
              <FormControlLabel value="CASH" control={<Radio />} label="Cash" />
              <FormControlLabel value="UPI" control={<Radio />} label="UPI" />
            </RadioGroup>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditPaymentDialog(false)} disabled={editPaymentSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditPaymentSave} disabled={editPaymentSaving}>
            {editPaymentSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Total Amount Dialog */}
      <Dialog open={editTotalDialog} onClose={() => setEditTotalDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Invoice Total</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" mb={2.5}>
            Update the course fee or apply/change a discount. The final total must be at least ₹{fmt(existingInvoice?.totalPaid)} (already paid).
          </Typography>
          <TextField
            label="Original Course Fee (₹)"
            type="number"
            fullWidth
            value={editOriginalAmount}
            onChange={(e) => setEditOriginalAmount(e.target.value)}
            InputProps={{ inputProps: { min: 1 } }}
            sx={{ mb: 2 }}
            autoFocus
          />
          <TextField
            label="Discount (₹)"
            type="number"
            fullWidth
            value={editDiscount}
            onChange={(e) => setEditDiscount(e.target.value)}
            InputProps={{ inputProps: { min: 0 } }}
            sx={{ mb: 2 }}
            helperText="Leave as 0 if no discount applies."
          />
          {Number(editOriginalAmount) > 0 && (
            <Box sx={{ p: 1.5, bgcolor: editComputedTotal > 0 ? 'primary.50' : 'error.50', borderRadius: 1.5, border: '1px solid', borderColor: editComputedTotal > 0 ? 'primary.light' : 'error.light' }}>
              <Box display="flex" gap={3} alignItems="center" flexWrap="wrap">
                {Number(editDiscount) > 0 && (
                  <>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Original</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ textDecoration: 'line-through', color: 'text.secondary' }}>
                        &#8377;{fmt(Number(editOriginalAmount))}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary">Discount</Typography>
                      <Typography variant="body2" fontWeight={600} color="warning.dark">- &#8377;{fmt(Number(editDiscount))}</Typography>
                    </Box>
                  </>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">Final Total</Typography>
                  <Typography variant="body1" fontWeight={700} color={editComputedTotal > 0 ? 'primary.main' : 'error.main'}>
                    &#8377;{fmt(editComputedTotal)}
                  </Typography>
                </Box>
              </Box>
              {editComputedTotal < (existingInvoice?.totalPaid || 0) && editComputedTotal > 0 && (
                <Typography variant="caption" color="error" display="block" mt={0.5}>
                  Cannot be less than amount already paid (₹{fmt(existingInvoice?.totalPaid)}).
                </Typography>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditTotalDialog(false)} disabled={editTotalSaving}>Cancel</Button>
          <Button variant="contained" onClick={handleEditTotalSave} disabled={editTotalSaving}>
            {editTotalSaving ? 'Saving...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default CreateInvoice
